/* Level 2: tabs directly under the hero (NAV1). Active tab: navy text, cyan underline. */
.bar {
  position: sticky;
  top: 0;
  z-index: 5;
  border-bottom: 1px solid var(--border-card);
  background: var(--white);
  box-shadow: 0 2px 6px var(--navy-a05);
}

.inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 24px;
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 var(--content-gutter);
}

.listWrap {
  position: relative;
  display: flex;
  flex: 1 1 320px;
  min-width: 0;
}

/* QA-052: the tabs never shrink; on a narrow screen the bar scrolls sideways inside itself. */
.list {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: nowrap;
  min-width: 0;
  margin: 0;
  padding: 0;
  overflow-x: auto;
  scrollbar-width: thin;
  list-style: none;
}

/* A fade at an end that has more tabs past it. It's a hint only; every tab is a link either way. */
.listWrap::before,
.listWrap::after {
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  width: 32px;
  content: '';
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--dur-fast) ease;
}

.listWrap::before {
  left: 0;
  background: linear-gradient(to right, var(--white), transparent);
}

.listWrap::after {
  right: 0;
  background: linear-gradient(to left, var(--white), transparent);
}

.listWrap[data-before='true']::before,
.listWrap[data-after='true']::after {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .listWrap::before,
  .listWrap::after {
    transition: none;
  }
}

.tab {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 8px;
  /* A short label ("AI") still makes a 44 px target. */
  min-width: 44px;
  min-height: 56px;
  margin-right: 30px;
  padding: 0 2px;
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 13px;
  letter-spacing: 0.08em;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;
  transition: color var(--dur-fast) ease;
}

.tab:hover {
  color: var(--brand-navy);
}

.tab[aria-current='page'] {
  color: var(--brand-navy);
  font-weight: var(--fw-medium);
  box-shadow: inset 0 -3px 0 var(--brand-cyan);
}

.tab:focus-visible {
  outline-offset: -2px;
}

/*
 * APP-236 (option C, Tom 2026-10-29): Manage's groups as a row of pills above the tabs, in the same
 * sticky bar. The current group is filled navy; the others are outlined with a border that meets
 * 3:1. A thin divider follows the person's main work. On a narrow screen the row scrolls sideways
 * inside the bar, with the fade edges, like the tabs (QA-052).
 */
.groupsRow {
  border-bottom: 1px solid var(--gray-100);
}

.groupsWrap {
  position: relative;
  display: flex;
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 var(--content-gutter);
}

/* The fades sit over the row's own edge, inside the gutter's padding. */
.groupsWrap::before,
.groupsWrap::after {
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  width: 32px;
  content: '';
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--dur-fast) ease;
}

.groupsWrap::before {
  left: var(--content-gutter);
  background: linear-gradient(to right, var(--white), transparent);
}

.groupsWrap::after {
  right: var(--content-gutter);
  background: linear-gradient(to left, var(--white), transparent);
}

.groupsWrap[data-before='true']::before,
.groupsWrap[data-after='true']::after {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .groupsWrap::before,
  .groupsWrap::after {
    transition: none;
  }
}

/* The padding keeps each pill's focus ring inside the scrolling row, which would clip it. */
.groupsList {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 10px 6px;
  overflow-x: auto;
  scrollbar-width: thin;
}

.pill {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  min-width: 44px;
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid var(--border-strong);
  border-radius: 22px;
  background: var(--white);
  color: var(--brand-navy);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: var(--fw-semibold);
  letter-spacing: 0.14em;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;
  transition:
    background var(--dur-fast) ease,
    border-color var(--dur-fast) ease;
}

.pill:hover {
  border-color: var(--brand-navy);
}

.pill[aria-current='true'] {
  border-color: var(--brand-navy);
  background: var(--brand-navy);
  color: var(--white);
}

@media (prefers-reduced-motion: reduce) {
  .pill {
    transition: none;
  }
}

.divider {
  flex: 0 0 auto;
  width: 1px;
  height: 28px;
  margin: 0 10px;
  background: var(--gray-300);
}

/* Under a row of groups the page tabs are a little shorter, as the board draws them. */
.bar[data-groups='true'] .tab {
  min-height: 50px;
}

.count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--radius-pill);
  background: var(--gray-100);
  color: var(--brand-navy);
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: var(--fw-bold);
  letter-spacing: 0;
}

/*
 * APP-236 step 2: what waits on a Manage group or page, as the board draws it: gold-50 ground,
 * gold-200 border, gold text (--text-gold on --brand-gold-50, 6.4:1). The number is the eye's;
 * screen readers hear "<n> waiting" from the hidden words beside it.
 */
.waitingChip {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border: 1px solid var(--brand-gold-200);
  border-radius: 10px;
  background: var(--brand-gold-50);
  color: var(--text-gold);
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: var(--fw-bold);
  letter-spacing: 0;
  line-height: 1;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}
